import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Loader2, Plus, QrCode } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import { GuestPassQr } from "../../components/GuestPassQr";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  createGuestPass,
  getGuestPasses,
  revokeGuestPass,
  type GuestPass,
  type GuestPassStatus,
} from "../../lib/admin-api";
import { useToast } from "../../hooks/use-toast";

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function toLocalInputValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusLabel(status: GuestPassStatus): string {
  if (status === "active") return "有効";
  if (status === "expired") return "期限切れ";
  return "無効";
}

export default function GuestPasses() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const defaultExpiry = useMemo(() => {
    const next = new Date();
    next.setDate(next.getDate() + 1);
    next.setMinutes(0, 0, 0);
    return toLocalInputValue(next);
  }, []);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [label, setLabel] = useState("外部見学");
  const [expiresLocal, setExpiresLocal] = useState(defaultExpiry);
  const [created, setCreated] = useState<{ url: string; label: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "guest-passes"],
    queryFn: getGuestPasses,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createGuestPass({
        label,
        expiresAt: new Date(expiresLocal).toISOString(),
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "guest-passes"] });
      setCreated({ url: result.url, label: result.pass.label });
      toast({ title: "作成しました", description: "QRとURLはこの画面を閉じると再表示できません。" });
    },
    onError: (err: Error) => {
      toast({ title: "作成に失敗しました", description: err.message, variant: "destructive" });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: revokeGuestPass,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "guest-passes"] });
      toast({ title: "無効化しました", description: "以後このQRでは入場できません。" });
    },
    onError: (err: Error) => {
      toast({ title: "無効化に失敗しました", description: err.message, variant: "destructive" });
    },
  });

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "コピーしました", description: url });
    } catch {
      toast({ title: "コピーできませんでした", description: url, variant: "destructive" });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-extrabold tracking-tight">ゲスト入場</h1>
            <p className="mt-1 text-sm text-gray-500">
              Googleログインなしで、期限付きQRから学生として面接できます。
            </p>
          </div>
          <Dialog
            open={isCreateOpen}
            onOpenChange={(open) => {
              setIsCreateOpen(open);
              if (!open) setCreated(null);
            }}
          >
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                入場券を作る
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{created ? "QRを保存してください" : "ゲスト入場券"}</DialogTitle>
                <DialogDescription>
                  {created
                    ? "このQRとURLは再表示できません。印刷するか、今すぐコピーしてください。"
                    : "名前と期限を決めて、共通のQRを発行します。"}
                </DialogDescription>
              </DialogHeader>
              {created ? (
                <div className="space-y-4">
                  <div className="flex justify-center rounded-xl border border-gray-200 bg-white p-4">
                    <GuestPassQr value={created.url} />
                  </div>
                  <p className="text-sm font-bold">{created.label}</p>
                  <p className="break-all text-xs text-gray-500">{created.url}</p>
                  <Button variant="outline" className="w-full" onClick={() => copyUrl(created.url)}>
                    <Copy className="mr-2 h-4 w-4" />
                    URLをコピー
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="guest-pass-label">名前</Label>
                    <Input
                      id="guest-pass-label"
                      value={label}
                      onChange={(event) => setLabel(event.target.value)}
                      maxLength={80}
                      placeholder="見学 10/10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="guest-pass-expires">有効期限</Label>
                    <Input
                      id="guest-pass-expires"
                      type="datetime-local"
                      value={expiresLocal}
                      onChange={(event) => setExpiresLocal(event.target.value)}
                    />
                  </div>
                </div>
              )}
              <DialogFooter>
                {created ? (
                  <Button onClick={() => setIsCreateOpen(false)}>閉じる</Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                      キャンセル
                    </Button>
                    <Button
                      onClick={() => createMutation.mutate()}
                      disabled={createMutation.isPending || !label.trim() || !expiresLocal}
                    >
                      {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "発行"}
                    </Button>
                  </>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          {isLoading ? (
            <div className="flex items-center justify-center p-10 text-sm text-gray-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              読み込み中…
            </div>
          ) : error ? (
            <div className="p-6 text-sm text-rose-700">入場券を読み込めませんでした。</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>名前</TableHead>
                  <TableHead>状態</TableHead>
                  <TableHead>期限</TableHead>
                  <TableHead>入場数</TableHead>
                  <TableHead>作成</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.passes ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-sm text-gray-500">
                      まだ入場券はありません。
                    </TableCell>
                  </TableRow>
                ) : (
                  (data?.passes ?? []).map((pass: GuestPass) => (
                    <TableRow key={pass.id}>
                      <TableCell className="font-medium">{pass.label}</TableCell>
                      <TableCell>{statusLabel(pass.status)}</TableCell>
                      <TableCell>{formatDateTime(pass.expiresAt)}</TableCell>
                      <TableCell>{pass.entryCount}</TableCell>
                      <TableCell>
                        {formatDateTime(pass.created)}
                        {pass.createdByName ? ` / ${pass.createdByName}` : ""}
                      </TableCell>
                      <TableCell className="text-right">
                        {pass.status === "active" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={revokeMutation.isPending}
                            onClick={() => revokeMutation.mutate(pass.id)}
                          >
                            無効化
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400">
                            <QrCode className="mr-1 inline h-3 w-3" />
                            再表示不可
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
